import React, { useState, useMemo, useEffect, useContext, useCallback, useRef } from 'react';
import moment from 'moment-timezone';
import './style.scss';
import _ from 'lodash';
import DefaultChip from 'components/Utils/DefaultChip';
import { type Bubble } from '../../../../careand-redux/slices/ui/ui';
import Icon from '../../../Utils/NewIcon';
import type {
  Appointment,
  Patient,
  Prescription,
  PrescriptionMedication,
} from '../../../../generated/api';
import { useAppDispatch } from '../../../../careand-redux/useAppDispatch';
import { CustomLoadingDataSpinner } from '../../../Common/LoadingDataSpinner';
import { api } from '../../../../careand-redux/api/ApiThunks';
import useAssistant from '../../../../hooks/useAssistant';
import AIUtils from '../../../../services/AIUtils';
import { slimMedication } from '../../../../services/PrescriptionUtils';
import { store, useAppSelector } from '../../../../careand-redux/store';
import { uiActions } from '../../../../careand-redux/slices/ui/ui';
import { v4 as uuidV4 } from 'uuid';
import CPPUtils from '../../../../services/CPPUtils';
import { createSelector } from '@reduxjs/toolkit';
import { tryParseJSON } from '../../../../services/FunctionUtils';

type Props = {
  patient: Patient;
  bubble: Bubble;
};

const CACHE_TTL = 600_000; //10 min

export function useGeneratePrescription(
  appointment: Appointment,
  patientId: string,
  note?: string,
) {
  const appointmentId = appointment.id!;
  const dispatch = useAppDispatch();
  const { runSavedPromptTyped, streamingText } = useAssistant('prescription-generator');
  const now = useMemo(() => moment().unix(), []);
  const cache = useAppSelector(
    (state) => state.ui.medicationSuggestionsByAppointmentId[appointment?.id ?? ''],
  );
  const invalidCache = !cache || now - cache.date >= CACHE_TTL;
  const medications = cache?.medications ?? [];
  const isLoading = cache?.isLoading ?? false;

  const setLoading = useCallback(
    (isLoading: boolean) => {
      dispatch(
        uiActions.updateMedicationSuggestions({ isLoading, appointmentId, date: moment().unix() }),
      );
    },
    [dispatch, appointmentId],
  );

  const addMedication = useCallback(
    (tool: { toolUse: any; toolResult: any }) => {
      if (tool.toolResult.isSuccess) {
        dispatch(
          uiActions.addMedicationSuggestion({
            medication: tool.toolUse.input.medication,
            appointmentId,
          }),
        );
      }
    },
    [dispatch, appointmentId],
  );

  const reset = useStreamingToolParser(streamingText ?? '', 'add_medication', addMedication);

  const resetMedications = useCallback(() => {
    dispatch(uiActions.updateMedicationSuggestions({ appointmentId, medications: [] }));
    reset();
  }, [dispatch, appointmentId]);

  const generatePrescription = useCallback(async () => {
    setLoading(true);
    resetMedications();

    const state = store.getState();
    const prescriptions = state.healthRecords.byId[patientId]?.prescriptions;
    // Unique tags across the practitioner's favorites — a cheap "menu" the model
    // can search_drug by (e.g. "headache") to pull their favorites for a category.
    const favoriteTags = _.uniq(
      (state.drugs.favoriteMedications ?? []).flatMap((f: any) => f?.tags ?? []),
    ).join(', ');
    const allergies =
      state.healthRecords.byId[patientId]?.healthRecords?.allergies
        ?.filter((allergy) => CPPUtils.isHealthRecordItemEnabled(allergy))
        ?.map((allergy) => allergy.value.name)
        ?.join(', ') || 'NKA';

    let transcription = '';

    if (appointmentId) {
      const res = await dispatch(
        api.Appointments.getAppointmentTranscription({ id: appointmentId }),
      );
      const text = res?.messages?.map((msg) => `${msg.from}: ${msg.text}`)?.join('\n');
      if (text && text.length > 0) {
        transcription = text;
      }
    }

    const draft =
      note ??
      (await (async () => {
        const drafts = (await dispatch(api.HealthRecords.getOwnDrafts())) ?? [];
        return _.orderBy(drafts, ['date'], ['desc'])[0]?.note.value.value ?? '';
      })());

    // The model only needs each previous prescription's DRUG info (it reuses the
    // exact drug data) + a recency hint — not the full Prescription object
    // (pharmacy, attachments, audit fields) and not pretty-printing. The full
    // dump was a big chunk of this prompt's ~186k input tokens/call.
    const prettyPrescriptions = JSON.stringify(
      _.orderBy(prescriptions ?? [], ['date'], ['desc'])
        .slice(0, 15)
        .map((it) => ({
          date: moment.unix(it.date).fromNow(),
          medications: (it.medications ?? []).map(slimMedication),
        })),
    );

    setLoading(true);

    await runSavedPromptTyped(
      'Generate Prescription',
      {
        note: draft,
        // Compact (no pretty-print) — same data, ~25% fewer tokens.
        appointment: JSON.stringify(appointment),
        transcription,
        prescriptions: prettyPrescriptions,
        allergies,
        // Favorites themselves are no longer dumped — the model retrieves them on
        // demand via search_drug ({ favorites, drugbank }). We only pass the unique
        // TAGS so it knows what categories it can search by.
        favoriteTags,
        additionalInstructions: 'N/A',
      },
      { patientId },
    );

    setLoading(false);
  }, [appointment, patientId, dispatch, note, appointmentId, setLoading, resetMedications]);

  const result = useMemo(() => {
    return {
      generatePrescription,
      loading: isLoading,
      medications,
      invalidCache,
    } as const;
  }, [generatePrescription, isLoading, JSON.stringify(medications), invalidCache]);

  return result;
}

function useStreamingToolParser(
  input: string,
  toolName: string,
  cb: (tool: { toolUse: any; toolResult: any }) => void,
) {
  const pendingToolUses = useRef<any[]>([]);
  const offset = useRef(0);

  const reset = useCallback(() => {
    offset.current = 0;
    pendingToolUses.current = [];
  }, []);

  useEffect(() => {
    const text = input.slice(offset.current);
    offset.current = input.length;

    const toolResults: Record<string, any> = {};

    AIUtils.generateJSONSections(text).forEach((tool: any) => {
      if (!tool) {
        return;
      }
      if (tool.type === 'tool_use' && tool.name !== toolName) {
        return;
      }

      if (tool.type === 'tool_use') {
        pendingToolUses.current.push(tool);
      } else {
        toolResults[tool.tool_use_id] = tool;
      }
    });

    const matches = _(pendingToolUses.current)
      .map((toolUse: any) => {
        const toolResult = toolResults[toolUse.id];
        if (!toolResult) {
          return null;
        }
        return { toolUse, toolResult };
      })
      .compact()
      .value();

    pendingToolUses.current = pendingToolUses.current.filter((it) => !toolResults[it.id]);

    matches.forEach(cb);
  }, [input, toolName]);

  return reset;
}

const AISuggestionsBar = ({ patient, bubble }: Props) => {
  return null;
};

export default AISuggestionsBar;
